<template>
  <v-card flat class="px-6">
    <v-card-text>
      <v-container>
        <!-- Validation Alert -->
        <v-row>
          <v-col cols="12">
            <v-alert v-if="validationErrors.length > 0" type="error" class="mb-4">
              <ul>
                <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
              </ul>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row v-if="!hideActions" class="mb-4">
          <v-col cols="12" class="d-flex justify-end gap-3" style="gap: 12px;">
            <v-btn
              v-if="isEditMode && orderId"
              color="primary"
              outlined
              large
              :loading="downloading"
              @click="downloadPDF"
            >
              <v-icon left>mdi-download</v-icon>
              {{ $t('tslOrders.downloadPDF') }}
            </v-btn>
            <v-btn
              color="primary"
              large
              :loading="saving"
              :disabled="!isValid"
              @click="handleSave"
            >
              {{ isEditMode ? $t('tslOrders.saveOrder') : $t('tslOrders.addOrder') }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- PDF Preview Mode -->
        <div v-if="showPdfPreview" class="pdf-preview-container">
          <v-progress-circular v-if="loadingPdf" indeterminate color="primary" size="64" class="my-10" />
          <object
            v-else-if="pdfBlobUrl"
            :data="pdfBlobUrl"
            type="application/pdf"
            width="100%"
            height="100%"
            class="pdf-viewer"
          >
            <p class="text-center pa-4">
              {{ $t('tslOrders.pdfNotSupported') }}
            </p>
          </object>
          <v-alert v-else-if="!hideActions && validationErrors.length > 0" type="info" class="ma-4">
            {{ $t('tslOrders.messages.completePlacesToPreview') }}
          </v-alert>
          <v-alert v-else type="error" class="ma-4">
            {{ $t('tslOrders.pdfGenerationError') }}
          </v-alert>
        </div>

        <!-- HTML Preview Mode (original) -->
        <div v-else class="order-preview-container">
          <!-- Page 1 - Main Order Document -->
          <div class="a4-page">
          <!-- Header Section -->
          <!-- Route Section (Załadunki i Rozładunki - punkty 12-13) -->
          <div class="route-section">
            <div v-if="routePoints.length === 0" class="text-body-1 grey--text">-</div>
            <v-row v-else no-gutters class="route-columns">
              <!-- LEFT COLUMN - LOADING POINTS -->
              <v-col cols="12" md="6" class="route-column route-column-left">
                <div class="route-column-header">{{ $t('tslOrders.preview.loading') }}</div>
                <div v-for="(point, index) in loadingPoints" :key="'loading-' + index" class="route-point">
                  <div class="route-number">{{ index + 1 }}.</div>
                  <div class="route-details">
                    <div>
                      <strong>{{ formatDate(point.dateTime) }}</strong>
                      <span v-if="point.timeFrom"> {{ formatTime(point.timeFrom) }}</span>
                      <span v-if="point.timeTo"> - {{ formatTime(point.timeTo) }}</span>
                      <strong v-if="point.fix" class="ml-2 fix-badge">FIX</strong>
                    </div>
                    <div class="mt-1">{{ getLocationText(point.location) }}</div>
                    <div v-if="point.description" class="mt-1 route-description" v-html="point.description"></div>
                    <div v-if="point.weight || point.loadMeters" class="mt-1">
                      <span v-if="point.weight">{{ point.weight }} t</span>
                      <span v-if="point.loadMeters" class="ml-2">{{ point.loadMeters }} m</span>
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- RIGHT COLUMN - UNLOADING POINTS -->
              <v-col cols="12" md="6" class="route-column route-column-right">
                <div class="route-column-header">{{ $t('tslOrders.preview.unloading') }}</div>
                <div v-for="(point, index) in unloadingPoints" :key="'unloading-' + index" class="route-point">
                  <div class="route-number">{{ index + 1 }}.</div>
                  <div class="route-details">
                    <div>
                      <strong>{{ formatDate(point.dateTime) }}</strong>
                      <span v-if="point.timeFrom"> {{ formatTime(point.timeFrom) }}</span>
                      <span v-if="point.timeTo"> - {{ formatTime(point.timeTo) }}</span>
                      <strong v-if="point.fix" class="ml-2 fix-badge">FIX</strong>
                    </div>
                    <div class="mt-1">{{ getLocationText(point.location) }}</div>
                    <div v-if="point.description" class="mt-1 route-description" v-html="point.description"></div>
                    <div v-if="point.weight || point.loadMeters" class="mt-1">
                      <span v-if="point.weight">{{ point.weight }} t</span>
                      <span v-if="point.loadMeters" class="ml-2">{{ point.loadMeters }} m</span>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Cargo Description (punkt 14) -->
          <div v-if="formData.cargoDescription" class="section-box">
            <div class="section-title">{{ $t('tslOrders.preview.cargoDescription') }}</div>
            <div v-html="formData.cargoDescription"></div>
          </div>

          <!-- Carrier Notes/Annotations (punkt 17) -->
          <div v-if="formData.carrierNotes" class="section-box">
            <div class="section-title">{{ $t('tslOrders.preview.annotations') }}</div>
            <div v-html="formData.carrierNotes"></div>
          </div>

          <!-- Price and Payment (punkt 15) -->
          <div class="section-box">
            <div class="section-title">{{ $t('tslOrders.preview.priceAndPayment') }}</div>
            <div v-if="formData.carrierPrice !== null">
              <strong>{{ $t('tslOrders.preview.price') }}:</strong> {{ formData.carrierPrice }} {{ getCurrencyLabel(formData.carrierCurrency) }}
            </div>
            <div v-if="formData.carrierPaymentType !== null && formData.carrierPaymentType !== undefined">
              <strong>{{ $t('tslOrders.preview.payment') }}:</strong> {{ getPaymentTypeLabel(formData.carrierPaymentType) }}
              <span v-if="formData.carrierPaymentType === 1 && formData.carrierPaymentDays">
                ({{ formData.carrierPaymentDays }} {{ $t('tslOrders.preview.days') }})
              </span>
            </div>
          </div>

          <!-- Additional Notes (punkt 21) -->
          <div v-if="hasAdditionalNotes" class="section-box">
            <div class="section-title">{{ $t('tslOrders.preview.additionalNotes') }}</div>
            <div v-if="formData.notes">
              <strong>{{ $t('tslOrders.preview.notes') }}:</strong>
              <div v-html="formData.notes"></div>
            </div>
            <div v-if="formData.customsInfo" class="mt-2">
              <strong>{{ $t('tslOrders.preview.customs') }}:</strong> {{ formData.customsInfo }}
            </div>
            <div v-if="formData.cabotageInfo" class="mt-2">
              <strong>{{ $t('tslOrders.preview.cabotage') }}:</strong> {{ formData.cabotageInfo }}
            </div>
            <div v-if="formData.adrInfo" class="mt-2">
              <strong>{{ $t('tslOrders.preview.adr') }}:</strong> {{ formData.adrInfo }}
            </div>
          </div>

          <!-- Footer (punkt 19 + punkt 20 numeracja) -->
          <div class="order-footer">
            <div>{{ $t('tslOrders.preview.footer') }}</div>
            <div class="page-number">1/{{ totalPages }}</div>
          </div>
          </div><!-- End of Page 1 -->

          <!-- Page 2 - Order Conditions (punkt 22) -->
          <div v-if="configuration && configuration.orderConditions" class="a4-page">
            <div class="section-box">
              <div class="section-title">{{ $t('tslOrders.preview.orderConditions') }}</div>
              <div v-html="configuration.orderConditions"></div>
            </div>

            <!-- Footer with page number -->
            <div class="order-footer">
              <div>{{ $t('tslOrders.preview.footer') }}</div>
              <div class="page-number">2/{{ totalPages }}</div>
            </div>
          </div>

          <!-- Page 3 - Information Clause (punkt 23) -->
          <div v-if="configuration && configuration.informationClause" class="a4-page">
            <div class="section-box">
              <div class="section-title">{{ $t('tslOrders.preview.informationClause') }}</div>
              <div v-html="configuration.informationClause"></div>
            </div>

            <!-- Footer with page number -->
            <div class="order-footer">
              <div>{{ $t('tslOrders.preview.footer') }}</div>
              <div class="page-number">3/{{ totalPages }}</div>
            </div>
          </div>
        </div><!-- End of order-preview-container -->
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import Service from '@/services'
import { fileUrl, getEnumDescription } from '@/utils/helpers'

export default {
  name: 'PreviewTab',
  components: {
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    clientContractor: {
      type: Object,
      default: null
    },
    carrierContractor: {
      type: Object,
      default: null
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    orderNumber: {
      type: String,
      default: null
    },
    orderId: {
      type: String,
      default: null
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      saving: false,
      downloading: false,
      configuration: null,
      company: null,
      issuingUser: null,
      showPdfPreview: false,
      loadingPdf: false,
      pdfBlobUrl: null
    }
  },
  computed: {
    routePoints () {
      if (!this.formData.places || !Array.isArray(this.formData.places)) {
        return []
      }
      return [...this.formData.places].sort((a, b) => a.order - b.order)
    },
    loadingPoints () {
      return this.routePoints.filter(point => point.type === 0)
    },
    unloadingPoints () {
      return this.routePoints.filter(point => point.type === 1)
    },
    validationErrors () {
      const errors = []
      if (!this.formData.clientContractorId) {
        errors.push(this.$t('tslOrders.messages.clientRequired'))
      }
      if (!this.formData.carrierContractorId) {
        errors.push(this.$t('tslOrders.messages.carrierRequired'))
      }
      if (!this.formData.clientPrice || this.formData.clientPrice <= 0) {
        errors.push(this.$t('tslOrders.messages.clientPriceRequired'))
      }
      if (this.formData.clientCurrency === null || this.formData.clientCurrency === undefined) {
        errors.push(this.$t('tslOrders.messages.clientCurrencyRequired'))
      }
      if (!this.formData.carrierPrice || this.formData.carrierPrice <= 0) {
        errors.push(this.$t('tslOrders.messages.carrierPriceRequired'))
      }
      if (this.formData.carrierCurrency === null || this.formData.carrierCurrency === undefined) {
        errors.push(this.$t('tslOrders.messages.carrierCurrencyRequired'))
      }

      // Route validation
      if (!this.formData.places || this.formData.places.length === 0) {
        errors.push(this.$t('tslOrders.messages.routeMustHaveAtLeastOnePoint'))
      } else {
        const sortedPoints = [...this.formData.places].sort((a, b) => a.order - b.order)
        const hasLoad = sortedPoints.some(p => p.type === 0)

        if (!hasLoad) {
          errors.push(this.$t('tslOrders.messages.routeMustHaveAtLeastOneLoadingLocation'))
        }
        if (sortedPoints.length > 0 && sortedPoints[0].type !== 0) {
          errors.push(this.$t('tslOrders.messages.firstPointMustBeLoadingLocation'))
        }

        const missingLocations = sortedPoints.some(p => !p.location || !p.location.country)
        if (missingLocations) {
          errors.push(this.$t('tslOrders.messages.allRoutePointsMustHaveLocation'))
        }

        const missingDates = sortedPoints.some(p => !p.dateTime)
        if (missingDates) {
          errors.push(this.$t('tslOrders.messages.allRoutePointsMustHaveDate'))
        }
      }

      return errors
    },
    isValid () {
      return this.validationErrors.length === 0
    },
    hasAdditionalNotes () {
      return this.formData.notes || this.formData.customsInfo || this.formData.cabotageInfo || this.formData.adrInfo
    },
    totalPages () {
      let pages = 1 // Main page always exists
      if (this.configuration && this.configuration.orderConditions) pages++
      if (this.configuration && this.configuration.informationClause) pages++
      return pages
    },
    uniqueOrderCode () {
      if (!this.company || !this.orderNumber) return ''

      // Compact format: CompanyDisplayId + YYMM + number (without separators)
      // Example: MER0022601123 instead of MER002-123/1/2026
      const date = this.formData.createdAt ? new Date(this.formData.createdAt) : new Date()
      const yy = date.getFullYear().toString().slice(-2)
      const mm = (date.getMonth() + 1).toString().padStart(2, '0')
      // Extract only the order number (first part before slash)
      const orderNum = this.orderNumber.split('/')[0]

      return `${this.company.displayId}${yy}${mm}${orderNum}`
    },
    barcodeTypeLabel () {
      if (!this.configuration) return ''
      return this.configuration.barcodeType === 2 ? 'QR CODE' : 'BARCODE'
    },
    allCurrencies () {
      return this.$store.getters.allCurrencies
    }
  },
  watch: {
    showPdfPreview (newVal) {
      if (newVal && !this.pdfBlobUrl) {
        this.generatePdfPreview()
      }
    },
    isActive (newVal, oldVal) {
      // Generuj PDF tylko gdy zakładka staje się aktywna
      if (newVal && !oldVal) {
        this.refreshPreview()
      }
    }
  },
  async mounted () {
    await this.loadConfiguration()
    await this.loadCompanyData()

    // Automatyczne generowanie PDF tylko jeśli zakładka jest aktywna
    if (this.isActive) {
      this.showPdfPreview = true
      await this.generatePdfPreview()
    }
  },
  beforeDestroy () {
    // Cleanup blob URL
    if (this.pdfBlobUrl) {
      window.URL.revokeObjectURL(this.pdfBlobUrl)
    }
  },
  methods: {
    async loadConfiguration () {
      try {
        const response = await Service.get('TSL/GetConfiguration')
        this.configuration = response.data
      } catch (error) {
        // Configuration might not exist yet
        console.warn('TSL Configuration not loaded:', error)
      }
    },
    async loadCompanyData () {
      const companyId = this.$store.getters.userCommonData.companyId
      const userId = this.$store.getters.userCommonData.id

      try {
        const response = await Service.get('Company/GetCompanyDetails', {
          companyId: companyId
        })
        if (response.data) {
          this.company = response.data
        }
      } catch (error) {
        console.error('Failed to load company data:', error)
      }

      try {
        const response = await Service.get('User/GetCompanyUserProfile', {
          UserId: userId
        })
        if (response.data) {
          this.issuingUser = response.data
        }
      } catch (error) {
        console.error('Failed to load user data:', error)
      }
    },
    getLogoUrl (url) {
      return fileUrl(url)
    },
    async downloadPDF () {
      if (!this.orderId) {
        await this.$store.dispatch('setErrorNotification', this.$t('tslOrders.messages.orderNotSaved'))
        return
      }

      try {
        this.downloading = true
        const response = await Service.getFile('TSLOrder/DownloadOrderPDF', {
          orderId: this.orderId
        })

        // Create download link - response.data is already a Blob
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Zlecenie_${this.orderNumber.replace(/\//g, '-')}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        await this.$store.dispatch('setErrorNotification', this.$t('tslOrders.messages.downloadError'))
      } finally {
        this.downloading = false
      }
    },
    getCurrencyLabel (currencyValue) {
      return getEnumDescription(this.allCurrencies, currencyValue)
    },
    handleSave () {
      if (this.isValid) {
        this.saving = true
        this.$emit('save')
      }
    },
    getPaymentTypeLabel (value) {
      if (value === null || value === undefined) return '-'
      const paymentTypes = {
        0: this.$t('tslOrders.paymentTypes.none'),
        1: this.$t('tslOrders.paymentTypes.deferred'),
        2: this.$t('tslOrders.paymentTypes.paymentOnUnloading'),
        3: this.$t('tslOrders.paymentTypes.paymentInAdvance')
      }
      return paymentTypes[value] || '-'
    },
    getLocationText (location) {
      if (!location || !location.place) return '-'
      const parts = []
      if (location.country) parts.push(location.country)
      if (location.place.postalCode) parts.push(location.place.postalCode)
      if (location.place.city) parts.push(location.place.city)
      return parts.length > 0 ? parts.join(', ') : '-'
    },
    formatDate (dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('pl-PL')
    },
    formatTime (timeSpan) {
      if (!timeSpan) return '-'
      return timeSpan.substring(0, 5) // Format HH:MM from HH:MM:SS
    },
    togglePreviewMode () {
      this.showPdfPreview = !this.showPdfPreview
    },
    async refreshPreview () {
      // Wyczyść stary blob URL
      if (this.pdfBlobUrl) {
        window.URL.revokeObjectURL(this.pdfBlobUrl)
        this.pdfBlobUrl = null
      }

      // Przeładuj dane i wygeneruj nowy podgląd
      await this.loadConfiguration()
      await this.loadCompanyData()

      if (this.showPdfPreview) {
        await this.generatePdfPreview()
      }
    },
    async regeneratePdfPreview () {
      // Wyczyść stary blob URL
      if (this.pdfBlobUrl) {
        window.URL.revokeObjectURL(this.pdfBlobUrl)
        this.pdfBlobUrl = null
      }

      // Wygeneruj nowy podgląd
      await this.generatePdfPreview()
    },
    async generatePdfPreview () {
      if (!this.formData) return

      // Walidacja przed generowaniem PDF (tylko dla trybu edycji/dodawania, nie dla dialogu z tabeli)
      if (!this.hideActions) {
        const validationErrors = this.validateFormDataForPreview()
        if (validationErrors.length > 0) {
          this.loadingPdf = false
          this.pdfBlobUrl = null
          return
        }
      }

      this.loadingPdf = true
      try {
        // Jeśli hideActions === true (dialog podglądu z tabeli), użyj GET endpoint z zapisanych danych
        // W przeciwnym razie (tryb edycji/dodawania), użyj POST endpoint z aktualnych danych formularza
        if (this.hideActions && this.orderId) {
          const response = await Service.getFile('TSLOrder/DownloadOrderPDF', {
            orderId: this.orderId
          })
          this.pdfBlobUrl = window.URL.createObjectURL(response.data)
        } else {
          // Tryb edycji/dodawania -> użyj POST endpoint z aktualnymi danymi formularza
          // WAŻNE: Wykluczamy orderId z payload, bo backend wtedy ignoruje dane formularza i ładuje z bazy!
          const { orderId, ...formDataWithoutOrderId } = this.formData
          const payload = {
            ...formDataWithoutOrderId,
            clientContractorId: this.formData.clientContractorId,
            carrierContractorId: this.formData.carrierContractorId
          }
          const response = await Service.postFileBlob('TSLOrder/PreviewOrderPDF', payload)
          this.pdfBlobUrl = window.URL.createObjectURL(response.data)
        }
      } catch (error) {
        await this.$store.dispatch('setErrorNotification',
          this.$t('tslOrders.messages.pdfPreviewError'))
        this.pdfBlobUrl = null
      } finally {
        this.loadingPdf = false
      }
    },
    validateFormDataForPreview () {
      const errors = []

      // Sprawdź czy są punkty trasy
      if (!this.formData.places || this.formData.places.length === 0) {
        return errors // Brak punktów - nie generujemy błędu, po prostu nie pokazujemy podglądu
      }

      // Sprawdź czy wszystkie punkty trasy mają wypełnione wymagane pola
      const hasInvalidPlaces = this.formData.places.some(place => {
        return !place.location ||
               !place.location.country ||
               !place.location.place ||
               !place.dateTime
      })

      if (hasInvalidPlaces) {
        errors.push('invalid_places')
      }

      return errors
    }
  }
}
</script>

<style scoped>
/* A4 Pages Container */
.order-preview-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;
  padding: 20px 0;
  background-color: #e0e0e0;
  min-height: 100vh;
}

/* PDF Preview Container */
.pdf-preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #e0e0e0;
  height: 80vh;
  overflow: hidden;
}

.pdf-viewer {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  background-color: white;
  height: 100%;
}

/* A4 Page */
.a4-page {
  width: 210mm;
  min-height: 297mm;
  padding: 20mm;
  background-color: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* Legacy support - keep for existing styles */
.order-preview {
  width: 100%;
  max-width: 100%;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 15px;
  border-bottom: 2px solid #333;
  margin-bottom: 20px;
}

.logo-section {
  flex: 0 0 150px;
}

.company-logo {
  max-width: 150px;
  max-height: 80px;
  object-fit: contain;
}

.header-text-section {
  flex: 1;
  text-align: center;
  padding: 0 20px;
}

.barcode-section {
  flex: 0 0 200px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
}

.barcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 120px;
}

.contact-info {
  font-size: 10pt;
  line-height: 1.4;
}

.contact-detail {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.email-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.bank-accounts-section {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
}

.bank-account {
  flex: 1;
  font-size: 9pt;
}

.three-column-section {
  border-top: 2px solid #333;
  border-bottom: 2px solid #333;
  margin-bottom: 15px;
  padding-top: 10px;
}

.column-section {
  padding: 30px 25px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.center-column {
  text-align: center;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.order-number-box {
  background-color: #f0f0f0;
  padding: 15px;
  border: 2px solid #333;
  display: inline-block;
  width: 100%;
  max-width: 280px;
}

.order-number-title {
  font-size: 11pt;
  font-weight: bold;
  margin-bottom: 5px;
}

.order-number-value {
  font-size: 11pt;
  font-weight: bold;
  margin-bottom: 5px;
}

.order-issue-date {
  font-size: 9pt;
}

.subsection {
  margin-top: 15px;
}

.subsection-title {
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 10pt;
}

.section-box {
  padding: 15px;
  margin-bottom: 5px;
}

.section-title {
  font-weight: bold;
  font-size: 12pt;
  background-color: #f0f0f0;
  padding: 8px;
  margin: -15px -15px 15px -15px;
}

.field-row {
  margin-bottom: 8px;
  line-height: 1.6;
  word-wrap: break-word;
}

.field-label {
  font-weight: 600;
  display: inline-block;
  min-width: 80px;
}

.field-value {
  display: inline;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.route-section {
  margin-bottom: 15px;
}

.route-columns {
  display: flex;
}

.route-column {
  padding: 15px 20px;
}

.route-column-left {
  border-right: 1px solid #333;
}

.route-column-header {
  font-weight: bold;
  margin-left: 10px;
  font-size: 11pt;
  margin-bottom: 15px;
  text-transform: uppercase;
}

.route-point {
  padding: 10px;
  margin: 10px;
  margin-bottom: 20px;
  background-color: #fafafa;
  border: 1px solid #ddd;
  display: flex;
  gap: 10px;
}

.route-number {
  font-weight: bold;
  font-size: 11pt;
  min-width: 25px;
}

.route-details {
  flex: 1;
  line-height: 1.6;
  font-size: 10pt;
}

.route-description {
  font-style: italic;
  color: #555;
}

.fix-badge {
  background-color: #333;
  color: white;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 9pt;
}

.section-box >>> p {
  margin: 0;
  padding: 0;
  line-height: 1.4;
}

.section-box >>> p + p {
  margin-top: 5px;
}

.order-footer {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-top: 1px solid #333;
  font-size: 9pt;
  color: #666;
}

.page-number {
  font-size: 9pt;
  color: #666;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 960px) {
  .order-preview-container {
    padding: 10px;
    background-color: white;
  }

  .a4-page {
    width: 100%;
    min-height: auto;
    padding: 15px;
    box-shadow: none;
    border: 1px solid #ddd;
  }

  .barcode-section {
    flex: 0 0 auto;
  }

  .barcode-container {
    max-width: 100px;
  }

  .column-section {
    padding: 15px 10px;
  }

  .field-label {
    min-width: 60px;
    font-size: 9pt;
  }

  .order-number-box {
    max-width: 100%;
  }

  .section-title {
    font-size: 10pt;
  }

  .route-column-left {
    border-right: none;
    border-bottom: 2px solid #333;
    padding-bottom: 20px;
  }

  .route-column {
    padding: 15px 10px;
  }

  .route-point {
    margin-bottom: 15px;
  }
}

@media print {
  .order-preview-container {
    background-color: white;
    padding: 0;
    gap: 0;
  }

  .a4-page {
    width: 210mm;
    height: 297mm;
    padding: 20mm;
    box-shadow: none;
    page-break-after: always;
    margin: 0;
  }

  .a4-page:last-child {
    page-break-after: avoid;
  }
}
</style>
